import React from "react";
import { Link } from "react-router-dom";
import TableCell from "@material-ui/core/TableCell";
import numberFormatter from "utils/numberFormatter";
import LoanTableOptionsMenu from "components/Global/OptionsMenu/LoanTable/LoanTableOptionsMenu";
import _ from "lodash";
import hedgeTypes from "constants/hedgeTypes";

import { calculateClosingCosts } from "./util";

const SupplementalLoanTableColumns = [
  {
    id: "caeda4ab-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: true,
    dataField: "loanProvider.name",
    numeric: false,
    label: "Lender",
    Cell: ({ record, callbacks }) => (
      <TableCell>
        {(
          <Link to={`/loan/${callbacks.getDealId()}/${record.id}`}>
            {record["loanProvider.name"] || "Lender Not Selected"}
          </Link>
        ) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "caeda4d4-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "amount",
    numeric: false,
    label: "Total Loan Amount",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.maskCurrency(record.amount) || "\u2014"}
      </TableCell>
    )
  },

  {
    id: "caeda4f0-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "covenants.ltvStabilized",
    numeric: false,
    label: "LTV",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.formatRate(_.get(record, "covenants.ltvStabilized"), 2, "\u2014") || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "caeda4ff-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "loanTermMonths",
    numeric: false,
    label: "Term",
    Cell: ({ record }) => (
      <TableCell>{record.loanTermMonths || "\u2014"}</TableCell>
    )
  },
  {
    id: "caeda510-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "extensionTerms.extensionTermMonths",
    numeric: false,
    label: "Extensions",
    Cell: ({ record }) => (
      <TableCell>{_.get(record, "extensionTerms.extensionTermMonths") || "\u2014"}</TableCell>
    )
  },
  {
    id: "caeda523-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "loanPricing",
    numeric: false,
    label: "Loan Pricing",
    Cell: ({ record }) => <TableCell>{ _.get(record, "loanPricing") || "\u2014"}</TableCell>
  },
  {
    id: "caeda537-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "currentRate",
    numeric: false,
    label: "Current Rate",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.formatRate(record.currentRate, 2, "\u2014")}
      </TableCell>
    )
  },
  {
    id: "caeda54d-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "amortizationTerm",
    numeric: false,
    label: "Amortization Term",
    Cell: ({ record }) => (
      <TableCell>{record.amortizationTerm || "\u2014"}</TableCell>
    )
  },
  {
    id: "caeda565-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "ioPeriod",
    numeric: false,
    label: "I/O Periods",
    Cell: ({ record }) => <TableCell>{record.ioPeriod || "\u2014"}</TableCell>
  },
  {
    id: "caeda57e-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "interestOnlyPayment",
    numeric: false,
    label: "I/O Payment",
    Cell: ({ record }) => <TableCell>{ numberFormatter.maskCurrency(record.interestOnlyPayment) || "\u2014"}</TableCell>
  },
  {
    id: "caeda59a-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "amortizingPayment",
    numeric: false,
    label: "Amortizing Payment",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.maskCurrency(record.amortizingPayment) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "caeda5b7-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "prefHedge.hedgeTypeId",
    numeric: false,
    label: "Hedge Type",
    Cell: ({ record }) => {
      if (!_.get(record, "prefHedge.hedgeTypeId")) {
        return <TableCell>{"\u2014"}</TableCell>;
      }

      return (
        <TableCell>
          {
            hedgeTypes.list.filter(x => x.value === _.get(record, "prefHedge.hedgeTypeId"))[0] ?
              hedgeTypes.list.filter(x => x.value === _.get(record, "prefHedge.hedgeTypeId"))[0].name : "Unknown"
          }
        </TableCell>
      );
    }
  },
  {
    id: "caeda5d5-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "prefHedge.marketCost",
    numeric: false,
    label: "Hedge Premium",
    Cell: ({ record }) => {
      const hedgeType = _.get(record, "prefHedge.hedgeTypeId", "\u2014");
      if (hedgeType === hedgeTypes.CAP) {
        return (<TableCell>{numberFormatter.maskCurrency(_.get(record, "prefHedge.marketCost"), "\u2014", true)}</TableCell>);
      } else if (hedgeType === hedgeTypes.SWAP) {
        return (<TableCell>{numberFormatter.formatRate(_.get(record, "prefHedge.marketRate"), 2, "\u2014") }</TableCell>);
      }

      return (<TableCell>{"\u2014"}</TableCell>);
    }
  },
  {
    id: "caeda5f6-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "closingCosts.totalAmount",
    numeric: false,
    label: "Closing Costs",
    Cell: ({ record }) => <TableCell>{numberFormatter.maskCurrency(_.get(record, "closingCosts.totalAmount", "\u2014"))}</TableCell>
  },
  {
    id: "caeda618-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "loanRecourse.recourseType.name",
    numeric: false,
    label: "Recourse",
    Cell: ({ record }) => (
      <TableCell>{record["loanRecourse.recourseType.name"] || "\u2014"}</TableCell>
    )
  },
  {
    id: "caeda63c-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "prepaymentTerms.prepaymentType.value",
    numeric: false,
    label: "Prepayment Type",
    Cell: ({ record }) => (
      <TableCell>{_.get(record, "prepaymentTerms.prepaymentType.value") || "\u2014"}</TableCell>
    )
  },
  {
    id: "caeda662-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "covenants.dscrStabilized",
    numeric: false,
    label: "DSCR",
    Cell: ({ record }) => (
      <TableCell>{record["covenants.dscrStabilized"] ? `${record["covenants.dscrStabilized"]}x` : "\u2014"}</TableCell>
    )
  },
  {
    id: "caeda689-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "covenants.debtYieldStabilized",
    numeric: false,
    label: "Debt Yield",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.formatRate(record["covenants.debtYieldStabilized"], 2, "\u2014")}
      </TableCell>
    )
  },
  {
    id: "caeda6b3-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "effectiveRate",
    numeric: false,
    label: "Effective Rate",
    Cell: ({ record }) => <TableCell>{numberFormatter.formatRate(_.get(record, "effectiveRate", null), 2, "\u2014")}</TableCell>
  },
  {
    id: "caeda6de-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "averageProjectedRate",
    numeric: false,
    label: "Projected Average Rate",
    Cell: ({ record }) => <TableCell>{numberFormatter.formatRate(_.get(record, "averageProjectedRate", null), 2, "\u2014")}</TableCell>
  },
  {
    id: "caeda76e-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "loanType.name",
    numeric: false,
    label: "Quote Type",
    Cell: ({ record }) => (
      <TableCell>{record["loanType.name"] || "\u2014"}</TableCell>
    )
  },
  {
    id: "caeda4e3-4a53-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "initialProceeds",
    numeric: false,
    label: "Initial Proceeds",
    Cell: ({ record }) => <TableCell>{numberFormatter.maskCurrency(_.get(record, "initialProceeds", "\u2014"))}</TableCell>
  },
  {
    id: "menu",
    isBaseColumn: true,
    dataField: null,
    layoutOrder: 999,
    numeric: false,
    Cell: ({ record, callbacks }) => (
      <TableCell className="loansList_Menu" key="menu">
        <LoanTableOptionsMenu
          id={record.id}
          expirationCallback={callbacks.expired}
          refreshGridCallback={callbacks.refreshGrid}
          tableName={"SupplementalLoanTableColumns"}
        />
      </TableCell>
    )
  }
];

export default SupplementalLoanTableColumns;
